/*
 * "Continue with Google" (docs/api.md, "Sign in with Google"): Google's mark, the button (a link the browser follows
 * to the API, which sends it on to Google and back), and the screen an org shows in place of its pages when it asks
 * its people to sign in with Google and this session didn't (Sign-in domains).
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { api, isApiError } from "../../api/client";
import { googleFailure, googleStart, hereForGoogle, useGoogleHere } from "../../api/google";
import { useMe } from "../../api/queries";
import type { MeOrg } from "../../api/types";
import { cx } from "../../lib/cx";
import { useDocumentTitle } from "../../lib/hooks";
import { buttonClass, type ButtonSize } from "../../ui/Button";
import { EmptyState } from "../../ui/EmptyState";
import { FormError } from "../../ui/Field";
import { Spinner } from "../../ui/icons";

/** Google's "G", in its own colors (Google's sign-in button guidelines). */
export function GoogleMark({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false" className={cx("shrink-0", className)}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** The button: a link to the API's start, so the whole page goes to Google. A spinner while it does. */
export function GoogleButton({ href, children = "Continue with Google", size = "lg", className }: { href: string; children?: string; size?: ButtonSize; className?: string }) {
  const [going, setGoing] = useState(false);
  // Back from Google with the browser's Back button (a page restored from its cache): the button again.
  useEffect(() => {
    const reset = () => setGoing(false);
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);
  const mark = size === "sm" ? 15 : 18;
  return (
    <a href={href} onClick={() => setGoing(true)} aria-busy={going || undefined} className={buttonClass("secondary", size, className)}>
      {going ? <Spinner size={mark} /> : <GoogleMark size={mark} />}
      {children}
    </a>
  );
}

/** "or" between two ways in. */
export function OrDivider() {
  return (
    <div className="my-5 flex items-center gap-3 text-[12px] text-dim" aria-hidden="true">
      <span className="h-px flex-1 bg-line" />
      or
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

/**
 * In place of an org's pages while it asks this session for Google: the API's own words (which org asks, and where
 * Google works when it isn't offered at this address), and "Continue with Google" back to this page.
 */
export function GoogleGate({ org }: { org: MeOrg }) {
  useDocumentTitle(`Sign in with Google for ${org.name}`);
  const here = useGoogleHere();
  const me = useMe();
  const [params] = useSearchParams();
  // The org's own answer says who asks (its agency, for a client's page) and why.
  const q = useQuery({ queryKey: ["org", org.id, "google-gate"], queryFn: ({ signal }) => api.get(`/orgs/${org.id}`, signal), retry: false });
  const why = isApiError(q.error) && q.error.code === "google_required" ? q.error.message : null;
  const failure = googleFailure(params.get("google"), { email: me.data?.user.email });
  return (
    <div className="card">
      <EmptyState
        heading="h1"
        icon={<GoogleMark size={20} />}
        title={`Sign in with Google to open ${org.name}`}
        action={here ? <GoogleButton size="md" href={googleStart({ for: "link", next: hereForGoogle() })} /> : null}
      >
        {failure ? (
          <div className="mb-3 text-left">
            <FormError>{failure}</FormError>
          </div>
        ) : null}
        {why ?? (q.isPending ? " " : `${org.name} asks its people to sign in with Google.`)}
      </EmptyState>
    </div>
  );
}
